/**
 * UTM Parameter Tracking Utilities
 * 
 * Captures and persists UTM parameters for marketing attribution.
 * Uses localStorage for client-side persistence across page navigations.
 */

export interface UtmParams {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  landingPage?: string
  referrer?: string
}

const UTM_STORAGE_KEY = 'iperform_utm_params'
const LANDING_PAGE_KEY = 'iperform_landing_page'

/**
 * Parse UTM parameters from a URL
 * Supports both search params and hash params
 * Also detects Google Ads click IDs (gclid) and infers UTM params
 */
export function parseUtmParams(url: string, referrer?: string): UtmParams | null {
  try {
    const urlObj = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    const params = urlObj.searchParams

    const utmParams: UtmParams = {}
    let hasUtm = false

    // Extract standard UTM parameters
    const utmSource = params.get('utm_source')
    if (utmSource) {
      utmParams.utmSource = utmSource
      hasUtm = true
    }

    const utmMedium = params.get('utm_medium')
    if (utmMedium) {
      utmParams.utmMedium = utmMedium
      hasUtm = true
    }

    const utmCampaign = params.get('utm_campaign')
    if (utmCampaign) {
      utmParams.utmCampaign = utmCampaign
      hasUtm = true
    }

    const utmTerm = params.get('utm_term')
    if (utmTerm) {
      utmParams.utmTerm = utmTerm
      hasUtm = true
    }

    const utmContent = params.get('utm_content')
    if (utmContent) {
      utmParams.utmContent = utmContent
      hasUtm = true
    }

    // Check for Google Click ID (gclid) - indicates Google Ads traffic
    // Google Ads auto-tagging adds gclid but may not add UTM params
    const gclid = params.get('gclid')
    if (gclid && !hasUtm) {
      // Infer Google Ads as the source if no explicit UTM params
      utmParams.utmSource = 'google'
      utmParams.utmMedium = 'cpc'
      utmParams.utmCampaign = 'google_ads_auto'
      utmParams.utmContent = gclid // Store gclid for reference
      hasUtm = true
      console.log('[UTM Tracking] Detected Google Ads click (gclid), inferring UTM params')
    }

    // Check for Microsoft/Bing Click ID (msclkid)
    const msclkid = params.get('msclkid')
    if (msclkid && !hasUtm) {
      utmParams.utmSource = 'bing'
      utmParams.utmMedium = 'cpc'
      utmParams.utmCampaign = 'bing_ads_auto'
      utmParams.utmContent = msclkid
      hasUtm = true
      console.log('[UTM Tracking] Detected Bing Ads click (msclkid), inferring UTM params')
    }

    // Check for Facebook Click ID (fbclid)
    const fbclid = params.get('fbclid')
    if (fbclid && !hasUtm) {
      utmParams.utmSource = 'facebook'
      utmParams.utmMedium = 'social'
      utmParams.utmCampaign = 'facebook_ads_auto'
      utmParams.utmContent = fbclid
      hasUtm = true
      console.log('[UTM Tracking] Detected Facebook click (fbclid), inferring UTM params')
    }

    // If we found UTM params, capture landing page and referrer
    if (hasUtm) {
      utmParams.landingPage = urlObj.pathname + urlObj.search
      if (referrer) {
        utmParams.referrer = referrer
      }
      return utmParams
    }

    return null
  } catch (error) {
    console.warn('[UTM Tracking] Failed to parse URL:', error)
    return null
  }
}

/**
 * Store UTM parameters in localStorage
 * First-touch attribution: only stores if no params exist yet
 */
export function storeUtmParams(params: UtmParams): void {
  if (typeof window === 'undefined') return

  try {
    // Check if we already have UTM params (first-touch attribution)
    const existing = getStoredUtmParams()
    if (existing) {
      // Already have UTM params, don't override (first-touch wins)
      return
    }

    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params))
  } catch (error) {
    console.warn('[UTM Tracking] Failed to store UTM params:', error)
  }
}

/**
 * Retrieve stored UTM parameters from localStorage
 */
export function getStoredUtmParams(): UtmParams | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY)
    if (!stored) return null

    return JSON.parse(stored) as UtmParams
  } catch (error) {
    console.warn('[UTM Tracking] Failed to retrieve UTM params:', error)
    return null
  }
}

/**
 * Clear stored UTM parameters
 * Call this after successful conversion to prevent re-attribution
 */
export function clearUtmParams(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(UTM_STORAGE_KEY)
  } catch (error) {
    console.warn('[UTM Tracking] Failed to clear UTM params:', error)
  }
}

/**
 * Get stored landing page (first page visited in this browser session)
 */
function getStoredLandingPage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return sessionStorage.getItem(LANDING_PAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Store landing page if not already set (tracks first page of session)
 */
function storeLandingPage(path: string): void {
  if (typeof window === 'undefined') return
  try {
    if (!sessionStorage.getItem(LANDING_PAGE_KEY)) {
      sessionStorage.setItem(LANDING_PAGE_KEY, path)
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Check if a path is an artist page (not a system path)
 */
function isArtistPagePath(path: string): { isArtist: boolean; handle?: string } {
  const systemPaths = ['/admin', '/api', '/login', '/signup', '/settings', '/select', '/dashboard', '/onboarding', '/referrals', '/reset-password', '/invite', '/contact', '/demo', '/discover', '/privacy', '/terms', '/embed', '/how-it-works', '/guest-book-complete']
  const isSystemPath = systemPaths.some(p => path.startsWith(p))
  
  if (isSystemPath) return { isArtist: false }
  
  if (!path.match(/^\/[a-z0-9._-]+$/i)) return { isArtist: false }
  
  const handle = path.slice(1).toLowerCase()
  
  // Reserved handles that are NOT artist pages
  const reservedHandles = new Set([
    'login', 'logout', 'signout', 'signin', 'signup', 'register', 'onboarding',
    'dashboard', 'api', 'terms', 'privacy', 'select-artist', 'select', 'create-artist',
    'guest-book-complete', 'referrals', 'how-it-works', 'interactive-demo', 'embed',
    'admin', 'administrator', 'help', 'support', 'about', 'contact', 'settings',
    'profile', 'account', 'user', 'users', 'artist', 'artists', 'event', 'events',
    'live', 'search', 'explore', 'discover', 'home', 'index', 'blog', 'news',
    'faq', 'pricing', 'features', 'analytics', 'songs', 'demo', 'invite'
  ])
  
  if (reservedHandles.has(handle)) return { isArtist: false }
  
  return { isArtist: true, handle }
}

/**
 * Check if a path is an event page
 */
function isEventPagePath(path: string): { isEvent: boolean; artistHandle?: string; eventId?: string } {
  const eventMatch = path.match(/^\/([a-z0-9._-]+)\/events\/([a-z0-9-]+)$/i)
  if (eventMatch) {
    const [, artistHandle, eventId] = eventMatch
    return { isEvent: true, artistHandle: artistHandle.toLowerCase(), eventId }
  }
  return { isEvent: false }
}

/**
 * Determine attribution based on landing page context
 * 
 * Attribution rules:
 * 1. If visitor has explicit UTM params → use those (handled elsewhere)
 * 2. If visitor's LANDING PAGE was an artist/event page → attribute to that artist/event
 * 3. If visitor came from external referrer → attribute as referral
 * 4. Otherwise → organic traffic
 * 
 * Key insight: We only attribute to "artist" if the artist page was the ENTRY POINT
 * to the site. If someone lands on homepage and then browses to an artist page,
 * they're organic visitors, not artist referrals.
 */
function inferUtmFromLandingContext(): UtmParams | null {
  if (typeof window === 'undefined') return null

  const currentPath = window.location.pathname
  const referrer = document.referrer || ''
  
  // Store the landing page (first page of this session)
  storeLandingPage(currentPath)
  
  // Get the actual landing page (might be this page or a previous one in session)
  const landingPage = getStoredLandingPage() || currentPath
  
  // Check if landing page was an artist page
  const artistCheck = isArtistPagePath(landingPage)
  if (artistCheck.isArtist && artistCheck.handle) {
    // Visitor entered site via artist page - this is an artist referral
    // This typically happens when someone scans an artist's QR code
    return {
      utmSource: 'artist',
      utmMedium: 'referral',
      utmCampaign: artistCheck.handle,
      landingPage: landingPage,
      referrer: referrer || undefined,
    }
  }
  
  // Check if landing page was an event page
  const eventCheck = isEventPagePath(landingPage)
  if (eventCheck.isEvent && eventCheck.artistHandle && eventCheck.eventId) {
    // Visitor entered site via event page - this is an event referral
    return {
      utmSource: 'event',
      utmMedium: 'referral',
      utmCampaign: `${eventCheck.artistHandle}:${eventCheck.eventId}`,
      landingPage: landingPage,
      referrer: referrer || undefined,
    }
  }
  
  // Not an artist/event landing - check if there's an external referrer
  if (referrer) {
    try {
      const referrerUrl = new URL(referrer)
      const currentHost = window.location.hostname
      
      // Only count as referral if from external domain
      if (referrerUrl.hostname !== currentHost && !referrerUrl.hostname.endsWith(`.${currentHost}`)) {
        // External referrer - attribute as referral traffic
        const referrerDomain = referrerUrl.hostname.replace('www.', '')
        return {
          utmSource: referrerDomain,
          utmMedium: 'referral',
          landingPage: landingPage,
          referrer: referrer,
        }
      }
    } catch {
      // Invalid referrer URL, ignore
    }
  }
  
  // No UTM params, not an artist/event landing, no external referrer
  // This is organic/direct traffic
  return {
    utmSource: 'direct',
    utmMedium: 'organic',
    landingPage: landingPage,
    referrer: referrer || undefined,
  }
}

/**
 * Capture UTM parameters from current page URL or infer from landing context
 * Returns params if found, stores them, and returns
 * 
 * Attribution priority:
 * 1. Existing stored params (first-touch wins)
 * 2. Explicit UTM params in URL
 * 3. Inferred from landing context (artist page entry, external referrer, or organic)
 */
export function captureUtmParams(): UtmParams | null {
  if (typeof window === 'undefined') return null

  // First, always track the landing page for this session (even before checking existing params)
  storeLandingPage(window.location.pathname)

  // Check if we already have stored params (first-touch attribution)
  const existing = getStoredUtmParams()
  if (existing) {
    return existing
  }

  // Try to parse explicit UTM params from URL first
  const urlParams = parseUtmParams(
    window.location.href,
    document.referrer || undefined
  )

  if (urlParams) {
    storeUtmParams(urlParams)
    console.log('[UTM Tracking] Captured explicit UTM params:', urlParams.utmSource)
    return urlParams
  }

  // If no explicit URL params, infer from landing context
  // This handles: artist page entry, event page entry, external referrer, or organic/direct
  const inferredParams = inferUtmFromLandingContext()
  if (inferredParams) {
    storeUtmParams(inferredParams)
    console.log('[UTM Tracking] Inferred attribution:', inferredParams.utmSource, '/', inferredParams.utmMedium)
    return inferredParams
  }

  // Should not reach here as inferUtmFromLandingContext always returns something
  console.log('[UTM Tracking] No attribution determined for path:', window.location.pathname)
  return null
}

/**
 * React hook for automatic UTM tracking
 * Captures UTM params on mount and stores them
 */
export function useUtmTracking(): { utmParams: UtmParams | null } {
  // For SSR compatibility, don't use useState
  // Just return current params from localStorage
  if (typeof window === 'undefined') {
    return { utmParams: null }
  }

  // Capture on first render (if not already captured)
  const params = captureUtmParams()

  return { utmParams: params }
}

